/**
 * Where a hand-added stream source lives, and who can see it.
 *
 * "Add Stream Source" writes to `localStorage`, so a pasted URL is browser-local
 * by construction — no other operator and no other machine ever sees it. Until
 * 2026-09-04 it was also stored under **one global key**, with no user and no
 * group in it, so a feed pasted while in one group stayed in the list in every
 * other group of every account signed in on that browser. The reported symptom
 * was the `abandoned_bag` showcase loop appearing under *Demo — Refinery
 * Security*; the cause was that nothing had ever scoped it to a group at all.
 *
 * Two things follow from the key shape below:
 *
 * - **The user id is in the key, not only the group.** A pasted URL is stored
 *   in plaintext and an RTSP one carries camera credentials in its userinfo, so
 *   a shared browser must not hand the next account the previous one's cameras.
 * - **A source derived from server state is never persisted.** `cam-rtmp-*`,
 *   `unit-feed-*`, `phone-*` and `hls-*` ids are rebuilt on every fetch from
 *   rows the backend already scopes; writing them here would resurrect a
 *   deleted sensor's feed as an undeletable hand-added one.
 *
 * There is deliberately no migration off the legacy key: its contents cannot be
 * attributed to a group after the fact, and guessing would put a feed back in
 * exactly the group this exists to keep it out of.
 */
import { isPullSource, type PullType } from './streamTypes';

export interface StoredSource {
  id: string;
  name: string;
  url: string;
  type: PullType;
}

/** The single ungrouped key used before 2026-09-04. Read by nothing; cleared by hand. */
export const LEGACY_SOURCES_KEY = 'toc_streamSources';

/** Id prefixes the dashboard derives from server state — see the note above. */
export const MANAGED_ID_PREFIXES = ['cam-rtmp-', 'unit-feed-', 'phone-', 'hls-', 'picam-'];

/** Is this list entry one an operator typed in, rather than one a fetch produced? */
export function isUserAddedSource(source: { id: string; type?: string }): boolean {
  return isPullSource(source.type) && !MANAGED_ID_PREFIXES.some(p => source.id.startsWith(p));
}

/** The storage key for one account in one group. Both parts are load-bearing. */
export function sourcesKey(userId?: number | null, groupId?: number | null): string {
  return `${LEGACY_SOURCES_KEY}:u${userId ?? 'none'}:g${groupId ?? 'none'}`;
}

/** Narrow unknown JSON to the entries we are prepared to render. */
function asSource(value: unknown): StoredSource | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.id !== 'string' || !v.id) return null;
  if (typeof v.name !== 'string' || typeof v.url !== 'string') return null;
  const type = typeof v.type === 'string' ? v.type : undefined;
  if (!isPullSource(type)) return null;
  if (MANAGED_ID_PREFIXES.some(p => (v.id as string).startsWith(p))) return null;
  return { id: v.id, name: v.name, url: v.url, type };
}

/**
 * Read one account+group's hand-added sources.
 *
 * Never throws: a browser with storage disabled, or a key holding anything but
 * the expected array, yields an empty list rather than an unmountable panel.
 */
export function readSources(
  storage: Pick<Storage, 'getItem'> | null | undefined,
  userId?: number | null,
  groupId?: number | null,
): StoredSource[] {
  try {
    const raw = storage?.getItem(sourcesKey(userId, groupId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(asSource).filter((s): s is StoredSource => s !== null);
  } catch {
    return [];
  }
}

/** Persist one account+group's hand-added sources; an empty list removes the key. */
export function writeSources(
  storage: Pick<Storage, 'setItem' | 'removeItem'> | null | undefined,
  userId?: number | null,
  groupId?: number | null,
  sources: Array<{ id: string; name: string; url: string; type: string }> = [],
): void {
  const key = sourcesKey(userId, groupId);
  try {
    const keep = sources.filter(isUserAddedSource);
    if (keep.length > 0) {
      storage?.setItem(key, JSON.stringify(keep.map(s => ({
        id: s.id, name: s.name, url: s.url, type: s.type,
      }))));
    } else {
      storage?.removeItem(key);
    }
  } catch {
    /* storage full or disabled — the in-memory list still works for this session */
  }
}
